/**
 * Nutzlasten der Schnittstelle, wie das Frontend sie tatsächlich verwendet.
 *
 * Warum von Hand und nicht direkt aus `schema.d.ts`? Die erzeugte Datei ist
 * nicht eingecheckt (siehe .gitignore). Griffe der Anwendungscode direkt darauf
 * zu, ließe sich das Projekt nach dem Klonen nicht übersetzen, bevor jemand
 * `pnpm gen:api` ausgeführt hat — der Übersetzungsfehler sähe dann wie ein
 * Codefehler aus.
 *
 * Damit die Handarbeit nicht auseinanderläuft, prüft `src/api/contract.assert.ts`
 * jeden Typ hier gegen das erzeugte Schema. Auszuführen mit `pnpm check:api`.
 * Weicht das Backend ab, schlägt dieser Abgleich fehl — nicht erst der Betrieb.
 *
 * Zahlenwerte mit Dezimalstellen kommen als Zeichenkette. Das ist Absicht des
 * Backends (Pydantic `Decimal`): Über JSON-Fließkommazahlen wären Flächen und
 * Beträge nicht verlustfrei übertragbar.
 */

export type Json = { readonly [key: string]: unknown };

// --- Anmeldung ---------------------------------------------------------------

export interface OrganizationBrief {
  id: string;
  name: string;
  slug: string;
}

export interface LoginIn {
  email: string;
  password: string;
  organization_slug?: string | null;
  device_label?: string | null;
}

export interface TokenOut {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  organization: OrganizationBrief | null;
  available_organizations: OrganizationBrief[];
  mfa_setup_required: boolean;
}

export interface MfaChallengeOut {
  mfa_required: boolean;
  challenge_id: string;
  message: string;
  available_organizations: OrganizationBrief[];
}

export interface MfaVerifyIn {
  challenge_id: string;
  code: string;
  device_label?: string | null;
}

export interface RefreshIn {
  refresh_token: string;
}

export interface MeOut {
  id: string;
  email: string;
  display_name: string;
  organization: OrganizationBrief | null;
  role: string | null;
  permissions: string[];
  mfa_enabled: boolean;
  mfa_required_for_role: boolean;
  is_system_admin: boolean;
  locale: string;
  timezone: string;
}

/**
 * `POST /auth/login` antwortet mit zwei verschiedenen Formen. Unterschieden
 * wird über `mfa_required` — deshalb ist das Feld hier der Merkmalsträger.
 */
export type LoginResult = TokenOut | MfaChallengeOut;

export function isMfaChallenge(result: LoginResult): result is MfaChallengeOut {
  return "mfa_required" in result && result.mfa_required;
}

// --- Objektstruktur ---------------------------------------------------------

export type PropertyStatus = "planning" | "active" | "under_renovation" | "archived";

export type RoomStatus =
  | "not_assessed"
  | "already_renovated"
  | "demolition_in_progress"
  | "open_ready"
  | "demolition_required"
  | "inspection_required"
  | "renovation_planned"
  | "renovation_ordered"
  | "completed";

export type ConditionRating =
  | "good"
  | "acceptable"
  | "in_need_of_repair"
  | "defective"
  | "critical"
  | "unknown";

export type QuantitySource =
  | "plan_estimated"
  | "measured_confirmed"
  | "offer_stated"
  | "ai_estimated"
  | "manufacturer";

/** Menge mit Herkunft. `is_estimate` trennt Schätzung von Aufmaß (Kapitel 15.3). */
export interface QuantityOut {
  value: string;
  unit: string | null;
  formatted: string;
  source: QuantitySource | null;
  is_estimate: boolean;
}

/** Notfallkontakt am Objekt (Kapitel 10.4). */
export interface EmergencyContact {
  name: string;
  role: string | null;
  phone: string;
  /** Erreichbarkeit im Wortlaut, etwa „Mo–Fr 7–17 Uhr". */
  available: string | null;
}

/** Ein bekanntes Risiko am Objekt (Kapitel 10.4). */
export interface KnownRisk {
  kind: string;
  text: string;
  severity: "hoch" | "mittel" | "niedrig";
  source: string | null;
  /** Bestätigt oder nur gemeldet — die Unterscheidung aus Kapitel 12.6. */
  confirmed: boolean;
}

/**
 * Kompakte Objektreferenz für objektübergreifende Listen (Kapitel 2a, Punkt 8:
 * Chip-Pflicht).
 *
 * Jede Zeile einer objektübergreifenden Liste — Vorgänge, Aufgaben, Rundgänge,
 * Medien, Dokumente, Termine — braucht Kürzel und Farbe des zugehörigen
 * Objekts direkt in der Zeile, als Chip. Ohne diese Referenz müsste die
 * Oberfläche sie pro Zeile nachladen; bei fünfzig Zeilen wären das fünfzig
 * zusätzliche Aufrufe, das Gegenteil von ADR 0001.
 *
 * `short_code` und `color_index` können `null` sein: bei einem Objekt, das vor
 * der Einführung des Kürzels angelegt wurde und noch keinen Backfill bekam.
 */
export interface PropertyRefOut {
  id: string;
  name: string;
  short_code: string | null;
  color_index: number | null;
}

export interface PropertyOut {
  id: string;
  name: string;
  code: string | null;
  /** Zwei Zeichen fürs Orientierungssystem — bei Anlage abgeleitet, durch die Verwaltung überschreibbar. */
  short_code: string | null;
  /** 0–6, feste Rotation nach Anlagereihenfolge über die sieben Statustöne. Nicht änderbar. */
  color_index: number | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string;
  full_address: string;
  construction_year: number | null;
  building_type: string | null;
  usage_type: string | null;
  status: PropertyStatus;
  technical_notes: string | null;
  /**
   * Bekannte Risiken am Objekt — strukturiert, nicht als formloses JSON.
   *
   * Stand hier als `Json[]`, was jede Auswertung unmöglich machte: Die
   * Oberfläche konnte einen Einstufungsgrad nicht anzeigen, ohne zu raten. Das
   * Backend führt den Typ längst genau; der Abgleich hat es aufgedeckt, sobald
   * er tatsächlich lief.
   */
  known_risks: KnownRisk[];
  /**
   * Notfallkontakte am Objekt — Name und Rufnummer sind das Wesentliche.
   *
   * Ebenfalls als `Json[]` geführt, obwohl das Backend die Struktur kennt. Eine
   * Rufnummer, die die Oberfläche nicht als solche erkennt, kann sie auch nicht
   * als Anruflink anbieten — im Notfall genau die Handlung, die zählt.
   */
  emergency_contacts: EmergencyContact[];
  emergency_instructions: string | null;
  portfolio_id: string | null;
  manager_user_id: string | null;
  custom_fields: Json;
  building_count: number;
  room_count: number;
  open_issue_count: number;
}

export interface BuildingOut {
  id: string;
  property_id: string;
  name: string;
  code: string | null;
  description: string | null;
  floor_count: number;
}

export interface FloorOut {
  id: string;
  building_id: string;
  name: string;
  level: number;
  area_sqm: string | null;
  plan_revision: string | null;
  access_notes: string | null;
  escape_routes: string | null;
  room_count: number;
}

export interface RoomOut {
  id: string;
  floor_id: string;
  property_id: string;
  name: string;
  number: string | null;
  previous_names: string[];
  area_sqm: string | null;
  wall_height_m: string | null;
  wall_area_sqm: QuantityOut | null;
  usage_type: string | null;
  condition: ConditionRating;
  renovation_status: RoomStatus;
  is_protected: boolean;
  protection_reason: string | null;
  access_rules: string | null;
  key_system: string | null;
  qr_code: string | null;
  responsible_user_id: string | null;
  custom_fields: Json;
  component_count: number;
  open_issue_count: number;
}

/**
 * `GET /properties/{id}/tree` liefert eine eigene, verdichtete Form — nicht die
 * vollen Raumobjekte. Das ist beabsichtigt: Ein Aufruf statt drei, damit die
 * Auswahl Objekt → Geschoss → Raum bei schwankender Verbindung durchläuft.
 * Das Backend gibt hier ein freies Wörterbuch aus, weshalb die Form unten von
 * Hand beschrieben ist und vom Vertragsabgleich nicht erfasst werden kann.
 */
export interface TreeRoom {
  id: string;
  name: string;
  number: string | null;
  renovation_status: RoomStatus;
  is_protected: boolean;
  area_sqm: number | null;
}

export interface TreeFloor {
  id: string;
  name: string;
  level: number;
  rooms: TreeRoom[];
}

export interface TreeBuilding {
  id: string;
  name: string;
  floors: TreeFloor[];
}

export interface PropertyTree {
  property: {
    id: string;
    name: string;
    address: string;
    status: PropertyStatus;
  };
  buildings: TreeBuilding[];
}

/**
 * Verlässlichkeit einer KI-Angabe (Kapitel 35).
 *
 * Stand dreimal getrennt in `comms.ts`, `issues.ts` und `transcribe.ts` und
 * zusätzlich viermal als bloßes `string` in Angeboten und Rechnungen. Ein
 * `string` an dieser Stelle ist nicht bloß ungenau: Er nimmt TypeScript die
 * Möglichkeit, einen Vergleich wie `confidence === "hoh"` zu beanstanden — die
 * Bedingung trifft dann stumm nie zu, und eine Angabe geringer Verlässlichkeit
 * wird ohne Warnhinweis angezeigt.
 */
export type Confidence = "high" | "medium" | "low";
